import { Component, computed, DestroyRef, inject, OnInit, signal, WritableSignal, ChangeDetectionStrategy, input, viewChild } from '@angular/core';
import { BreadCrumbService } from '../../../../@vex/components/breadcrumbs/BreadCrumbService';
import { Billing, BillingPeriod, BillingPeriodService, BillingService, CustomerAccessStatus, NoteService } from '@eadsysteme/billingclient-v1';
import { TranslocoDirective, TranslocoService } from '@jsverse/transloco';
import { Icons } from '../../../util/Icons';
import { SessionService } from '../../../services/session.service';
import { fadeInUp400ms } from '../../../../@vex/animations/fade-in-up.animation';
import { stagger40ms } from '../../../../@vex/animations/stagger.animation';
import { TableColumn } from '../../../../@vex/interfaces/table-column.interface';
import { ActivatedRoute, Router } from '@angular/router';
import { DateUtil } from '../../../util/DateUtil';
import { forkJoin, of, Subject } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { DateAdapter, MAT_DATE_FORMATS, MAT_DATE_LOCALE } from '@angular/material/core';
import { DayjsDateAdapter, MAT_DAYJS_DATE_ADAPTER_OPTIONS, MAT_DAYJS_DATE_FORMATS } from '../../../util/material-dayjs-adapter';
import { SnackbarService } from '../../../services/snackbar.service';

import { MatTab, MatTabContent, MatTabGroup } from '@angular/material/tabs';
import { MatIcon } from '@angular/material/icon';
import { CostsComponent } from './costs/costs.component';
import { BasicDataComponent } from './basic-data/basic-data.component';
import { MatButton, MatIconButton } from '@angular/material/button';
import { MatMenu, MatMenuItem, MatMenuTrigger } from '@angular/material/menu';
import { MatDialog } from '@angular/material/dialog';
import { MatTooltip } from '@angular/material/tooltip';
import { NotesDialogComponent } from './notes-dialog/notes-dialog.component';
import { TenantDetailCardsComponent } from '../../tenant-detail-cards/tenant-detail-cards.component';
import { ConfirmationDialogComponent } from '../../confirmation-dialog/confirmation-dialog.component';
import { PeriodCompletionDialogComponent } from './period-completion-dialog/period-completion-dialog.component';
import { MatCard, MatCardContent } from '@angular/material/card';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { finalize, map, switchMap } from 'rxjs/operators';
import { PeriodService } from './period.service';
import { DragToCompleteComponent } from './drag-to-complete/drag-to-complete.component';
import { NgTemplateOutlet } from '@angular/common';
import { MatBadge } from '@angular/material/badge';
import { PageLayoutComponent } from '../../../../@vex/components/page-layout/page-layout.component';
import { PageLayoutHeaderDirective } from '../../../../@vex/components/page-layout/page-layout-header.directive';
import { PageLayoutContentDirective } from '../../../../@vex/components/page-layout/page-layout-content.directive';
import { BreadcrumbsComponent } from '../../../../@vex/components/breadcrumbs/breadcrumbs.component';

@Component({
	selector: 'vex-billing',
	templateUrl: './billing.component.html',
	styleUrl: './billing.component.scss',
	animations: [fadeInUp400ms, stagger40ms],
	imports: [
		PageLayoutComponent,
		PageLayoutHeaderDirective,
		PageLayoutContentDirective,
		TranslocoDirective,
		BreadcrumbsComponent,
		MatTabGroup,
		MatTab,
		ReactiveFormsModule,
		MatIcon,
		CostsComponent,
		MatTabContent,
		BasicDataComponent,
		FormsModule,
		MatIconButton,
		MatMenu,
		MatMenuItem,
		MatMenuTrigger,
		MatTooltip,
		TenantDetailCardsComponent,
		MatButton,
		MatProgressSpinner,
		MatCard,
		MatCardContent,
		DragToCompleteComponent,
		NgTemplateOutlet,
		MatBadge,
	],
	changeDetection: ChangeDetectionStrategy.Eager,
	providers: [
		{ provide: DateAdapter, useClass: DayjsDateAdapter, deps: [MAT_DATE_LOCALE, MAT_DAYJS_DATE_ADAPTER_OPTIONS] },
		{ provide: MAT_DATE_FORMATS, useValue: MAT_DAYJS_DATE_FORMATS },
	],
})
export class BillingComponent implements OnInit {
	protected readonly breadCrumbService = inject(BreadCrumbService);
	protected readonly icons = inject(Icons);
	protected readonly dateUtil = inject(DateUtil);
	protected readonly sessionService = inject(SessionService);
	private readonly billingService = inject(BillingService);
	private readonly billingPeriodService = inject(BillingPeriodService);
	private readonly translocoService = inject(TranslocoService);
	protected readonly periodService = inject(PeriodService);
	private readonly route = inject(ActivatedRoute);
	private readonly router = inject(Router);
	private readonly formBuilder = inject(FormBuilder);
	private readonly snackBar = inject(SnackbarService);

	protected readonly CustomerAccessStatus = CustomerAccessStatus;

	readonly basicDataComponent = viewChild(BasicDataComponent);

	private readonly destroyRef = inject(DestroyRef);
	private readonly dialog = inject(MatDialog);
	private readonly noteService = inject(NoteService);

	billingId: string = undefined;

	readonly columnsBilling = input<TableColumn<BillingPeriod>[]>([
		{
			label: this.translocoService.translate('billing.name'),
			property: 'name',
			type: 'image',
			visible: true,
		},
		// {
		// 	label: this.translocoService.translate('billing.isLocked'),
		// 	property: 'isLocked',
		// 	type: 'text',
		// 	visible: true,
		// },
		{
			label: this.translocoService.translate('billing.customerAccessStatus'),
			property: 'customerAccessStatus',
			type: 'text',
			visible: true,
		},
		{
			label: this.translocoService.translate('billing.periodStatus'),
			property: 'periodStatus',
			type: 'text',
			visible: true,
		},
		{
			label: 'actions',
			property: 'actions',
			type: 'text',
			visible: true,
		},
	]);
	searchFilterSubject: Subject<string> = new Subject<string>();
	protected selectedTabIndex: WritableSignal<number> = signal(0);
	protected selectedBilling = signal<Billing>({ billingNumber: 0, realEstateId: null });
	protected periods: WritableSignal<BillingPeriod[]> = signal<BillingPeriod[]>([]);
	protected sortDirection: WritableSignal<'asc' | 'desc'> = signal<'asc'>('asc');
	protected billingFormGroup: FormGroup;
	protected noteCounts = signal<Record<string, number>>({});
	// Beide parallelen Requests (Billing + Periods) müssen fertig sein, bevor Header + Cards gemeinsam gerendert werden —
	// sonst kann durch unterschiedliche Response-Zeiten eine inkonsistente Zwischen-UI entstehen (Cards ohne Header o.ä.).
	protected readonly billingContentReady = computed(() => this.finishedLoading() && this.finishedLoadingPeriods());
	private readonly latestPeriod = signal<BillingPeriod>(undefined);
	finishedLoading = signal(false);
	finishedLoadingPeriods = signal(false);
	billingEditable = signal(false);
	highlightedPeriodId = signal('');

	constructor() {
		const routeParams = this.route.snapshot.paramMap;
		this.billingId = routeParams?.get('billingId');
		sessionStorage.setItem('selectedBillingId', this.billingId);
	}

	ngOnInit(): void {
		this.setBreadcrumbs();
		this.loadBilling();
		this.loadPeriods();
	}

	private createForm() {
		this.billingFormGroup = this.formBuilder.group(
			{
				billingName: [
					{
						value: this.selectedBilling()?.billingName,
						disabled: true,
					},
					[Validators.required, Validators.pattern(new RegExp(/^[\p{Letter}0-9\-_/()., ]{0,100}$/u))],
				],
				billingPeriodDuration: [
					{
						value: this.selectedBilling()?.billingPeriodDuration,
						disabled: true,
					},
					Validators.required,
				],
				portalActive: [{ value: this.selectedBilling()?.portalActive, disabled: true }, Validators.required],
				startTime: [{ value: this.selectedBilling()?.startTime ?? Date.now(), disabled: false }, Validators.required],
			},
			{
				validators: [],
			}
		);

		this.billingFormGroup.markAllAsTouched();
	}

	private setBreadcrumbs() {
		this.breadCrumbService.reset();
		this.breadCrumbService.addBreadCrumb({
			icon: this.icons.get('billing'),
			translationKey: 'billing.billingUnits',
		});
		this.breadCrumbService.addBreadCrumb({
			translationKey: 'billing.billingUnit',
			icon: this.icons.get('billingUnit'),
		});
		this.breadCrumbService.slice(0, 2);
	}

	private loadPeriods(onComplete?: () => void): void {
		this.finishedLoadingPeriods.set(false);
		this.billingPeriodService
			.readAllBillingPeriod(this.sessionService.effectiveArea, this.billingId)
			.pipe(
				switchMap((periods) =>
					periods.length === 0
						? of({ periods, counts: {} as Record<string, number> })
						: forkJoin(
								periods.map((p) =>
									this.noteService
										.readAllNote(this.sessionService.effectiveArea, this.billingId, p.exportId)
										.pipe(map((notes) => [p.exportId, notes?.length ?? 0] as const))
								)
							).pipe(map((entries) => ({ periods, counts: Object.fromEntries(entries) })))
				),
				takeUntilDestroyed(this.destroyRef),
				finalize(() => this.finishedLoadingPeriods.set(true))
			)
			.subscribe({
				next: ({ periods, counts }) => {
					this.periods.set(this.sortPeriods(periods));
					this.latestPeriod.set(periods?.sort((a, b) => b.validFrom.localeCompare(a.validFrom)).at(0));
					this.noteCounts.set(counts);
					onComplete?.();
				},
				error: () => {
					this.periods.set([]);
					this.noteCounts.set({});
				},
			});
	}

	protected toggleSort() {
		this.sortDirection.update((val) => (val === 'asc' ? 'desc' : 'asc'));
		this.periods.set(this.sortPeriods(this.periods()));
	}

	private sortPeriods(periods: BillingPeriod[]): BillingPeriod[] {
		if (!periods) return [];
		return [...periods].sort((a, b) => {
			return this.sortDirection() === 'desc' ? b.validFrom.localeCompare(a.validFrom) : a.validFrom.localeCompare(b.validFrom);
		});
	}

	private loadBilling() {
		if (!this.billingId) {
			this.snackBar.error(this.translocoService.translate('errors.loading'));
			this.router.navigate(['/billing-units']);
		}
		this.finishedLoading.set(false);
		this.billingService
			.readBilling(this.sessionService.effectiveArea, this.billingId)
			.pipe(
				takeUntilDestroyed(this.destroyRef),
				finalize(() => this.finishedLoading.set(true))
			)
			.subscribe({
				next: (billing: Billing) => {
					this.setBilling(billing);
				},
				error: () => {
					this.snackBar.error(this.translocoService.translate('errors.loading'));
					this.router.navigate(['/billing-units']);
				},
			});
	}

	private setBilling(billing: Billing) {
		this.selectedBilling.set(billing);
		this.billingEditable.set(false);
		this.createForm();
	}

	protected openCompletionDialog(period: BillingPeriod) {
		const oldLockDate = this.selectedBilling().lockPortalAccessUntil;
		const dialogRef = this.dialog.open(PeriodCompletionDialogComponent, {
			disableClose: false,
			autoFocus: false,
			restoreFocus: false,
			width: '800px',
			data: {
				period,
				billingId: this.billingId,
				realEstateId: this.selectedBilling().realEstateId,
				area: this.sessionService.effectiveArea,
				lockPortalAccessUntil: oldLockDate,
			},
		});

		dialogRef
			.afterClosed()
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe((result) => {
				if (result?.completed) {
					this.loadBilling();
					this.loadPeriods(() => this.openLockWarningDialog(result.period, oldLockDate));
				}
			});
	}

	private openLockWarningDialog(period: BillingPeriod, oldLockDate?: string): void {
		const shouldUpdateLock = !oldLockDate || period.validTo > oldLockDate;
		const lockDate = shouldUpdateLock ? period.validTo : oldLockDate;
		const formattedDate = this.dateUtil.setDateFormatByActiveLang(lockDate, '');
		const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
			disableClose: false,
			autoFocus: false,
			restoreFocus: false,
			width: '720px',
			maxWidth: '90vw',
		});
		dialogRef.componentInstance.titleMessage = this.translocoService.translate('billing.lockWarningDialog.title');
		const messageText = this.translocoService.translate('billing.lockWarningDialog.message');
		dialogRef.componentInstance.confirmMessage =
			`<div class="mt-3 flex w-full items-center gap-2.5 rounded bg-amber-50 p-4 text-[0.95rem] leading-[1.6] text-amber-900 sm:mx-6 border-l-4 border-amber-500">` +
			`<span class="material-icons h-[1.4rem] w-[1.4rem] shrink-0 text-[1.4rem] text-amber-500">warning</span>` +
			`<span class="flex-1 break-words whitespace-normal">${messageText} <strong class="font-bold">${formattedDate}</strong></span>` +
			`</div>`;
		dialogRef.componentInstance.showCloseButton = true;
		dialogRef.componentInstance.confirmButtonText = 'OK';
	}

	protected navigateToCosts(period: BillingPeriod) {
		this.periodService.selectedPeriod.set(period);
		this.selectedTabIndex.set(1);
	}

	protected navigateToSettings(period: BillingPeriod) {
		this.periodService.selectedPeriod.set(period);
		this.selectedTabIndex.set(3);
		const basicDataComponent = this.basicDataComponent();
		if (basicDataComponent) {
			basicDataComponent.selectedTabIndex.set(1);
		}
	}

	copyCost(period: BillingPeriod) {
		this.billingPeriodService
			.copyCostBillingPeriod(this.sessionService.effectiveArea, this.billingId, period.exportId)
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe({
				next: () => {
					this.loadPeriods();
					this.snackBar.success(this.translocoService.translate('billing.costsCopiedSuccess'));
				},
				error: () => {
					this.snackBar.error(this.translocoService.translate('errors.billingCopyCost'));
				},
			});
	}

	copyStock(period: BillingPeriod) {
		this.billingPeriodService
			.copyStockBillingPeriod(this.sessionService.effectiveArea, this.billingId, period.exportId)
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe({
				next: () => {
					this.loadPeriods();
					this.snackBar.success(this.translocoService.translate('billing.stockCopiedSuccess'));
				},
				error: () => {
					this.snackBar.error(this.translocoService.translate('errors.billingCopyStock'));
				},
			});
	}

	get visibleCardCount(): number {
		return this.periods().filter(
			(p) => p.customerAccessStatus === CustomerAccessStatus.ACTIVATED || p.customerAccessStatus === CustomerAccessStatus.SUBMITTED
		).length;
	}

	protected openNotes(period: BillingPeriod) {
		const dialogRef = this.dialog.open(NotesDialogComponent, {
			disableClose: true,
			autoFocus: false,
			restoreFocus: false,
			data: {
				period,
				billingId: this.billingId,
			},
			width: '800px',
		});

		dialogRef
			.afterClosed()
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe((updatedPeriod) => {
				if (updatedPeriod) {
					this.loadPeriods();
				}
			});
	}
}
